import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";

import { computeAvailableUnitsDiff, computeInventoryTotals, InventoryRow } from "./helper";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton, DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardSelect } from "@/dashboard-design-system/Select";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Add, Minus, Tag } from "@/icons/outlined";

export type { InventoryRow };

interface ShopInventoryModalProps {
  open: boolean;
  onClose: () => void;
  rateName: string;
  rows: InventoryRow[];
  onSave: (rows: InventoryRow[]) => void;
}

const ROW_HEIGHT = 92;
const HEADER_HEIGHT = 40;

interface AvailableUnitsDiffLabelProps {
  originalAvailableUnits: number | null;
  currentAvailableUnits: number | null;
}

const AvailableUnitsDiffLabel: FC<AvailableUnitsDiffLabelProps> = ({
  originalAvailableUnits,
  currentAvailableUnits,
}) => {
  const { t } = useTranslate();
  const diff = computeAvailableUnitsDiff(originalAvailableUnits, currentAvailableUnits);

  if (diff === 0) {
    return null;
  }

  const isPositive = diff > 0;

  return (
    <DashboardTypography variant="caption" vibe={isPositive ? "text-positive-quiet" : "text-danger-quiet"}>
      {isPositive
        ? t("dashboard", "ticketing.inventory.diffAdded", { count: diff })
        : t("dashboard", "ticketing.inventory.diffRemoved", { count: Math.abs(diff) })}
    </DashboardTypography>
  );
};

const PARENT_FILTER_ALL = "__all__";

export const ShopInventoryModal: FC<ShopInventoryModalProps> = ({ open, onClose, rateName, rows, onSave }) => {
  const { t } = useTranslate();
  const [editedRows, setEditedRows] = useState<InventoryRow[]>(rows);
  const [parentFilter, setParentFilter] = useState<string>(PARENT_FILTER_ALL);
  const originalRowsRef = useRef<InventoryRow[]>(rows);

  useEffect(() => {
    setEditedRows(rows);
    originalRowsRef.current = rows;
    setParentFilter(PARENT_FILTER_ALL);
  }, [rows]);

  const parentOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const row of rows) {
      if (row.parentId && row.parentName && !seen.has(row.parentId)) {
        seen.set(row.parentId, row.parentName);
      }
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [rows]);

  const showParentFilter = parentOptions.length > 1;

  const visibleRowIndexes = useMemo(() => {
    if (parentFilter === PARENT_FILTER_ALL) {
      return editedRows.map((_, index) => index);
    }
    return editedRows.reduce<number[]>((acc, row, index) => {
      if (row.parentId === parentFilter) {
        acc.push(index);
      }
      return acc;
    }, []);
  }, [editedRows, parentFilter]);

  const updateRowAtIndex = useCallback((rowIndex: number, updater: (row: InventoryRow) => InventoryRow) => {
    setEditedRows((previous) => previous.map((row, index) => (index === rowIndex ? updater(row) : row)));
  }, []);

  const handleAvailableUnitsChange = useCallback(
    (rowIndex: number, value: string) => {
      const parsed = parseInt(value, 10);
      updateRowAtIndex(rowIndex, (row) => ({
        ...row,
        availableUnits: isNaN(parsed) ? null : Math.max(0, parsed),
      }));
    },
    [updateRowAtIndex]
  );

  const handleIncrement = useCallback(
    (rowIndex: number) => {
      updateRowAtIndex(rowIndex, (row) => ({ ...row, availableUnits: (row.availableUnits ?? 0) + 1 }));
    },
    [updateRowAtIndex]
  );

  const handleDecrement = useCallback(
    (rowIndex: number) => {
      updateRowAtIndex(rowIndex, (row) => {
        if (row.availableUnits === null || row.availableUnits <= 0) {
          return row;
        }
        return { ...row, availableUnits: row.availableUnits - 1 };
      });
    },
    [updateRowAtIndex]
  );

  const handleSave = useCallback(() => {
    onSave(editedRows);
    onClose();
  }, [editedRows, onSave, onClose]);

  const hasMultipleRows = editedRows.length > 1;

  const totals = useMemo(() => computeInventoryTotals(editedRows), [editedRows]);

  const totalSummary =
    totals.totalAvailable !== null
      ? t("dashboard", "ticketing.inventory.totalSummary", {
          available: totals.totalAvailable,
          sold: totals.totalSold,
        })
      : t("dashboard", "ticketing.inventory.totalSummarySoldOnly", {
          sold: totals.totalSold,
        });

  return (
    <DashboardDialog open={open} onClose={onClose} maxWidth="md">
      <DashboardDialogBody
        slots={{
          header: (
            <Stack>
              <DashboardDialogHeader
                icon={<Tag />}
                title={t("dashboard", "ticketing.inventory.title", { name: rateName })}
                sx={{ borderBlockEnd: "none" }}
              />
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{ paddingX: 3, paddingBottom: 1 }}
              >
                <DashboardTypography variant="subtitle2" vibe="text-neutral-moderate">
                  {totalSummary}
                </DashboardTypography>
                {showParentFilter && (
                  <Box sx={{ minWidth: 200 }}>
                    <DashboardSelect
                      value={parentFilter}
                      onChange={(event) => setParentFilter(event.target.value as string)}
                    >
                      <MenuItem value={PARENT_FILTER_ALL}>
                        {t("dashboard", "ticketing.inventory.filterAllItems")}
                      </MenuItem>
                      {parentOptions.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </DashboardSelect>
                  </Box>
                )}
              </Stack>
            </Stack>
          ),
          content: (
            <DashboardDialogContent>
              <Stack spacing={0}>
                <Stack direction="row" alignItems="center" sx={{ height: HEADER_HEIGHT }}>
                  {hasMultipleRows && (
                    <Box sx={{ flex: 1, paddingLeft: 2 }}>
                      <DashboardTypography variant="body2" vibe="text-neutral-quiet">
                        {t("dashboard", "ticketing.inventory.variant")}
                      </DashboardTypography>
                    </Box>
                  )}
                  <Box sx={{ flex: 1, paddingLeft: 2 }}>
                    <DashboardTypography variant="body2" vibe="text-neutral-quiet">
                      {t("dashboard", "ticketing.inventory.unitsSold")}
                    </DashboardTypography>
                  </Box>
                  <Box sx={{ flex: 1, paddingLeft: 2 }}>
                    <DashboardTypography variant="body2" vibe="text-neutral-quiet">
                      {t("dashboard", "ticketing.inventory.availableUnits")}
                    </DashboardTypography>
                  </Box>
                </Stack>

                <Divider />

                {visibleRowIndexes.map((rowIndex, visibleIndex) => {
                  const row = editedRows[rowIndex];
                  if (!row) {
                    return null;
                  }
                  const displayName = row.parentName ? `${row.parentName} • ${row.name}` : row.name;
                  return (
                    <Box key={row.id}>
                      <Stack direction="row" alignItems="center" sx={{ height: ROW_HEIGHT }}>
                        {hasMultipleRows && (
                          <Box sx={{ flex: 1, paddingLeft: 2 }}>
                            <DashboardTypography variant="subtitle2" vibe="text-neutral-moderate">
                              {displayName}
                            </DashboardTypography>
                          </Box>
                        )}
                        <Box sx={{ flex: 1, paddingLeft: 2 }}>
                          <DashboardTypography variant="body2">{row.unitsSold}</DashboardTypography>
                        </Box>
                        <Box sx={{ flex: 1, paddingLeft: 2 }}>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <DashboardIconButton
                              size="medium"
                              vibe="neutral"
                              variant="text"
                              onClick={() => handleDecrement(rowIndex)}
                            >
                              <Minus />
                            </DashboardIconButton>
                            <Box sx={{ width: 88, position: "relative" }}>
                              <DashboardTextField
                                value={row.availableUnits ?? ""}
                                onChange={(event) => handleAvailableUnitsChange(rowIndex, event.target.value)}
                                placeholder={t("dashboard", "ticketing.inventory.unlimited")}
                                inputProps={{ style: { textAlign: "center" } }}
                              />
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: "100%",
                                  marginTop: 0.5,
                                  left: "50%",
                                  transform: "translateX(-50%)",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <AvailableUnitsDiffLabel
                                  originalAvailableUnits={originalRowsRef.current[rowIndex]?.availableUnits ?? null}
                                  currentAvailableUnits={row.availableUnits}
                                />
                              </Box>
                            </Box>
                            <DashboardIconButton
                              size="medium"
                              vibe="neutral"
                              variant="text"
                              onClick={() => handleIncrement(rowIndex)}
                            >
                              <Add />
                            </DashboardIconButton>
                          </Stack>
                        </Box>
                      </Stack>
                      {visibleIndex < visibleRowIndexes.length - 1 && <Divider />}
                    </Box>
                  );
                })}
              </Stack>
            </DashboardDialogContent>
          ),
          footer: (
            <DashboardDialogFooter>
              <DashboardButton onClick={handleSave}>{t("dashboard", "ticketing.inventory.save")}</DashboardButton>
              <DashboardButton onClick={onClose}>{t("dashboard", "ticketing.inventory.cancel")}</DashboardButton>
            </DashboardDialogFooter>
          ),
        }}
      />
    </DashboardDialog>
  );
};
